import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";

const STEPS = [
  { n: "01", t: "Describe requirements", d: "“Turn-based RPG with Elo matchmaking, Redis leaderboard and MySQL inventory.”" },
  { n: "02", t: "Twin analyzes & specs", d: "Requirements translated into technical specifications and a service topology." },
  { n: "03", t: "Generates architecture", d: "Golang services, PHP adapters, DB migrations, Dockerfiles and k8s manifests." },
  { n: "04", t: "Optimize & ship", d: "Root-cause analysis, caching and query tuning, ready for Linux deployment." },
];

export const DigitalTwin = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  return (
    <section id="digital-twin" className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-950/40 border-y border-slate-800/60">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <span className="text-xs font-mono uppercase tracking-widest text-emerald-400">The Digital Twin Engine</span>
          <h2 className="mt-3 font-display text-3xl lg:text-4xl font-bold tracking-tight text-slate-100">
            Your requirements in. Production backend out.
          </h2>
          <p className="mt-4 text-slate-400 leading-relaxed">
            The twin understands game-specific technologies — game server frameworks, real-time protocols and turn-based mechanics — and reasons from root cause to effective, optimized solutions.
          </p>
          <div className="mt-8 space-y-5">
            {STEPS.map((s) => (
              <div key={s.n} className="flex gap-4" data-testid={`twin-step-${s.n}`}>
                <div className="font-mono text-sm text-emerald-400 pt-0.5 w-8 shrink-0">{s.n}</div>
                <div>
                  <h4 className="text-slate-100 font-medium">{s.t}</h4>
                  <p className="text-sm text-slate-500 mt-0.5">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
          <Button onClick={() => (user ? navigate("/workspace") : login())} data-testid="twin-try-btn"
            className="mt-9 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold h-11 px-6 group">
            Try the agent <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>

        <div className="glass rounded-2xl border border-slate-800 p-6 shadow-2xl shadow-black/50">
          <div className="text-xs font-mono text-slate-500 mb-3">output · generated_plan.yaml</div>
          <pre className="text-[12px] font-mono leading-relaxed text-slate-300 overflow-x-auto bg-slate-950 rounded-lg p-4 border border-slate-800">
<span className="text-emerald-400">services:</span>{"\n"}
{"  "}<span className="text-cyan-400">match-service:</span>{"\n"}
{"    "}lang: go{"\n"}
{"    "}cache: redis{"\n"}
{"    "}protocol: grpc + websocket{"\n"}
{"  "}<span className="text-cyan-400">inventory-service:</span>{"\n"}
{"    "}lang: php{"\n"}
{"    "}db: mysql (indexed){"\n"}
<span className="text-emerald-400">deploy:</span>{"\n"}
{"  "}docker: true{"\n"}
{"  "}k8s: autoscale 3-20{"\n"}
          </pre>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            {["gRPC handlers", "Redis leaderboard", "MySQL migrations", "k8s manifests"].map((f) => (
              <div key={f} className="flex items-center gap-2 text-slate-400">
                <Check className="w-4 h-4 text-emerald-400" /> {f}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
